// Mock API implementation for development and testing

import type {
  Lead,
  Opportunity,
  Account,
  Contact,
  User,
  PipelineMetrics,
  LeadMetrics,
  SalesActivity,
  SalesforceApiResponse,
  QueryFilters,
  PaginationParams
} from '../../types/salesforce';

import {
  mockLeads,
  mockOpportunities,
  mockAccounts,
  mockUsers,
  mockPipelineMetrics,
  mockLeadMetrics,
  MockDataGenerator
} from '../mockData';

// Simulate network delay for realistic testing
const simulateNetworkDelay = (ms: number = 500): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

// Mock API that mimics the real Salesforce API structure
export class MockSalesforceApi {
  // Lead operations
  async getLeads(
    filters?: QueryFilters,
    pagination?: PaginationParams
  ): Promise<SalesforceApiResponse<Lead>> {
    await simulateNetworkDelay();

    let filteredLeads = [...mockLeads];

    // Apply filters
    if (filters?.dateRange) {
      const { start, end } = filters.dateRange;
      filteredLeads = filteredLeads.filter(lead => {
        const createdDate = new Date(lead.createdDate);
        return createdDate >= new Date(start) && createdDate <= new Date(end);
      });
    }

    if (filters?.leadSource && filters.leadSource.length > 0) {
      filteredLeads = filteredLeads.filter(lead =>
        filters.leadSource!.includes(lead.source)
      );
    }

    if (filters?.ownerId) {
      filteredLeads = filteredLeads.filter(lead => lead.assignedTo === filters.ownerId);
    }

    // Apply sorting
    if (pagination?.sortBy) {
      const { sortBy, sortOrder = 'ASC' } = pagination;
      filteredLeads.sort((a, b) => {
        const aValue = (a as any)[sortBy];
        const bValue = (b as any)[sortBy];

        if (aValue < bValue) return sortOrder === 'ASC' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'ASC' ? 1 : -1;
        return 0;
      });
    }

    // Apply pagination
    const totalSize = filteredLeads.length;
    const offset = pagination?.offset || 0;
    const limit = pagination?.limit || 50;
    const paginatedLeads = filteredLeads.slice(offset, offset + limit);

    return {
      totalSize,
      done: offset + limit >= totalSize,
      records: paginatedLeads,
      nextRecordsUrl: offset + limit < totalSize ? `/query?offset=${offset + limit}` : undefined,
    };
  }

  async getLeadById(id: string): Promise<Lead> {
    await simulateNetworkDelay(200);

    const lead = mockLeads.find(l => l.id === id);
    if (!lead) {
      throw new Error(`Lead with id ${id} not found`);
    }

    return lead;
  }

  async createLead(lead: Partial<Lead>): Promise<{ id: string; success: boolean }> {
    await simulateNetworkDelay(300);

    const newId = `lead_${Date.now()}`;

    // In a real implementation, this would create the lead in Salesforce
    console.log('Creating lead:', lead);

    return {
      id: newId,
      success: true,
    };
  }

  async updateLead(id: string, lead: Partial<Lead>): Promise<{ success: boolean }> {
    await simulateNetworkDelay(300);

    // Find and update the lead in our mock data
    const existingLeadIndex = mockLeads.findIndex(l => l.id === id);
    if (existingLeadIndex === -1) {
      throw new Error(`Lead with id ${id} not found`);
    }

    // Update the mock data
    mockLeads[existingLeadIndex] = { ...mockLeads[existingLeadIndex], ...lead };

    console.log('Updated lead:', id, lead);

    return { success: true };
  }

  // Opportunity operations
  async getOpportunities(
    filters?: QueryFilters,
    pagination?: PaginationParams
  ): Promise<SalesforceApiResponse<Opportunity>> {
    await simulateNetworkDelay();

    let filteredOpportunities = [...mockOpportunities];

    // Apply filters
    if (filters?.dateRange) {
      const { start, end } = filters.dateRange;
      filteredOpportunities = filteredOpportunities.filter(opp => {
        const createdDate = new Date(opp.createdDate);
        return createdDate >= new Date(start) && createdDate <= new Date(end);
      });
    }

    if (filters?.stage && filters.stage.length > 0) {
      filteredOpportunities = filteredOpportunities.filter(opp =>
        filters.stage!.includes(opp.stage)
      );
    }

    if (filters?.minAmount) {
      filteredOpportunities = filteredOpportunities.filter(opp => opp.amount >= filters.minAmount!);
    }

    if (filters?.maxAmount) {
      filteredOpportunities = filteredOpportunities.filter(opp => opp.amount <= filters.maxAmount!);
    }

    if (filters?.ownerId) {
      filteredOpportunities = filteredOpportunities.filter(opp => opp.ownerId === filters.ownerId);
    }

    // Apply sorting
    if (pagination?.sortBy) {
      const { sortBy, sortOrder = 'ASC' } = pagination;
      filteredOpportunities.sort((a, b) => {
        const aValue = (a as any)[sortBy];
        const bValue = (b as any)[sortBy];

        if (aValue < bValue) return sortOrder === 'ASC' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'ASC' ? 1 : -1;
        return 0;
      });
    }

    // Apply pagination
    const totalSize = filteredOpportunities.length;
    const offset = pagination?.offset || 0;
    const limit = pagination?.limit || 50;
    const paginatedOpportunities = filteredOpportunities.slice(offset, offset + limit);

    return {
      totalSize,
      done: offset + limit >= totalSize,
      records: paginatedOpportunities,
      nextRecordsUrl: offset + limit < totalSize ? `/query?offset=${offset + limit}` : undefined,
    };
  }

  async getOpportunityById(id: string): Promise<Opportunity> {
    await simulateNetworkDelay(200);

    const opportunity = mockOpportunities.find(o => o.id === id);
    if (!opportunity) {
      throw new Error(`Opportunity with id ${id} not found`);
    }

    return opportunity;
  }

  async updateOpportunity(id: string, opportunity: Partial<Opportunity>): Promise<{ success: boolean }> {
    await simulateNetworkDelay(300);

    const existingOppIndex = mockOpportunities.findIndex(o => o.id === id);
    if (existingOppIndex === -1) {
      throw new Error(`Opportunity with id ${id} not found`);
    }

    // Update the mock data
    mockOpportunities[existingOppIndex] = { ...mockOpportunities[existingOppIndex], ...opportunity };

    console.log('Updated opportunity:', id, opportunity);

    return { success: true };
  }

  // Analytics and metrics
  async getPipelineMetrics(filters?: QueryFilters): Promise<PipelineMetrics> {
    await simulateNetworkDelay(400);

    // Filter opportunities based on filters
    let opportunities = [...mockOpportunities];

    if (filters?.dateRange) {
      const { start, end } = filters.dateRange;
      opportunities = opportunities.filter(opp => {
        const createdDate = new Date(opp.createdDate);
        return createdDate >= new Date(start) && createdDate <= new Date(end);
      });
    }

    if (filters?.ownerId) {
      opportunities = opportunities.filter(opp => opp.ownerId === filters.ownerId);
    }

    // Calculate metrics from filtered data
    return MockDataGenerator.generatePipelineMetrics(opportunities);
  }

  async getLeadMetrics(filters?: QueryFilters): Promise<LeadMetrics> {
    await simulateNetworkDelay(400);

    // Filter leads based on filters
    let leads = [...mockLeads];

    if (filters?.dateRange) {
      const { start, end } = filters.dateRange;
      leads = leads.filter(lead => {
        const createdDate = new Date(lead.createdDate);
        return createdDate >= new Date(start) && createdDate <= new Date(end);
      });
    }

    if (filters?.ownerId) {
      leads = leads.filter(lead => lead.assignedTo === filters.ownerId);
    }

    // Calculate metrics from filtered data
    return MockDataGenerator.generateLeadMetrics(leads);
  }

  // Account operations
  async getAccounts(pagination?: PaginationParams): Promise<SalesforceApiResponse<Account>> {
    await simulateNetworkDelay();

    let accounts = [...mockAccounts];

    // Apply sorting
    if (pagination?.sortBy) {
      const { sortBy, sortOrder = 'ASC' } = pagination;
      accounts.sort((a, b) => {
        const aValue = (a as any)[sortBy];
        const bValue = (b as any)[sortBy];

        if (aValue < bValue) return sortOrder === 'ASC' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'ASC' ? 1 : -1;
        return 0;
      });
    }

    // Apply pagination
    const totalSize = accounts.length;
    const offset = pagination?.offset || 0;
    const limit = pagination?.limit || 50;
    const paginatedAccounts = accounts.slice(offset, offset + limit);

    return {
      totalSize,
      done: offset + limit >= totalSize,
      records: paginatedAccounts,
      nextRecordsUrl: offset + limit < totalSize ? `/query?offset=${offset + limit}` : undefined,
    };
  }

  // User operations
  async getUsers(): Promise<SalesforceApiResponse<User>> {
    await simulateNetworkDelay(200);

    return {
      totalSize: mockUsers.length,
      done: true,
      records: mockUsers,
    };
  }

  // Bulk operations
  async bulkUpdateLeads(updates: Array<{ id: string; data: Partial<Lead> }>): Promise<void> {
    await simulateNetworkDelay(1000);

    // Process updates in batches
    for (const update of updates) {
      await this.updateLead(update.id, update.data);
    }

    console.log(`Bulk updated ${updates.length} leads`);
  }

  // Simulate random failures for testing error handling
  async simulateError(errorType: 'network' | 'auth' | 'validation' = 'network'): Promise<never> {
    await simulateNetworkDelay(200);

    switch (errorType) {
      case 'network':
        throw new Error('Network error: Connection timeout');
      case 'auth':
        throw new Error('Authentication failed: Invalid token');
      case 'validation':
        throw new Error('Validation error: Required field missing');
      default:
        throw new Error('Unknown error occurred');
    }
  }
}

// Export singleton instance
export const mockSalesforceApi = new MockSalesforceApi();

// Mock React Query configuration for the mock API
export const mockSalesforceQueries = {
  leads: (filters?: QueryFilters, pagination?: PaginationParams) => ({
    queryKey: ['leads', filters, pagination],
    queryFn: () => mockSalesforceApi.getLeads(filters, pagination),
    staleTime: 5 * 60 * 1000, // 5 minutes
  }),

  opportunities: (filters?: QueryFilters, pagination?: PaginationParams) => ({
    queryKey: ['opportunities', filters, pagination],
    queryFn: () => mockSalesforceApi.getOpportunities(filters, pagination),
    staleTime: 5 * 60 * 1000,
  }),

  pipelineMetrics: (filters?: QueryFilters) => ({
    queryKey: ['pipeline-metrics', filters],
    queryFn: () => mockSalesforceApi.getPipelineMetrics(filters),
    staleTime: 10 * 60 * 1000, // 10 minutes for metrics
  }),

  leadMetrics: (filters?: QueryFilters) => ({
    queryKey: ['lead-metrics', filters],
    queryFn: () => mockSalesforceApi.getLeadMetrics(filters),
    staleTime: 10 * 60 * 1000,
  }),
};

export default mockSalesforceApi;